import clsx from "clsx";
import {
  type ComponentProps,
  FunctionComponent,
  useLayoutEffect,
  useRef,
} from "react";
import { graphql, useFragment } from "react-relay";

import TitleCellMedia from "@story/data-centers-hub/components/slides/TitleCellMedia";
import {
  ARIZONA_SHARE_FALLBACK_URL,
  arizonaCopy,
  arizonaSlideMedia,
} from "@story/data-centers-hub/config/arizonaAssets";
import { resolveLockupCopy } from "@story/data-centers-hub/config/lockupCopy";
import { wipeClipAnimAttrs } from "@story/data-centers-hub/config/wipeClipAnimAttrs";
import { asStill } from "@story/data-centers-hub/config/titleAssets";
import { useHubNarrowViewport } from "@story/data-centers-hub/hooks/useHubNarrowViewport";
import { useWipeChapterStage } from "@story/data-centers-hub/hooks/useWipeChapterStage";
import getBrowserInfo from "src/utils/get-browser-info";
import ShareButtons from "@voxmedia/duet-ui/components/article/ShareButtons";
import shareButtonsThevergeConfig from "@voxmedia/duet-ui/components/article/ShareButtons/config/ShareButtons.theverge.config";
import CommentCount from "@voxmedia/duet-ui/components/meta/CommentCount";
import { ArizonaSlideMeta_feedContentResource$key } from "src/__generated__/relay/ArizonaSlideMeta_feedContentResource.graphql";
import { useCskCommentCount } from "src/hooks/use-csk-comment-count";
import { useCskCommentsLinkUrl } from "src/hooks/use-csk-comments-link-url";
import { useCskCoralStoryId } from "src/hooks/use-csk-coral-story-id";
import { useShowStoryComments } from "src/hooks/use-show-story-comments";

import * as styles from "./ArizonaSlide.css";

interface Props {
  active: boolean;
  animate: boolean;
  /** Stay fully open while a later chapter covers us. */
  hold?: boolean;
  /** Play the exit wipe when returning toward Utah / earlier chapters. */
  depart?: boolean;
  /** Forward wipe in progress — keep media playing until it settles. */
  leaving?: boolean;
  /** Mount already open — session restore / soft remount (skip enter wipe). */
  resume?: boolean;
  /** Hide after the incoming wipe ends — blocks Android underlay flash. */
  muted?: boolean;
  /** Attach video `src` when this chapter is in the warm neighborhood. */
  mediaWarm?: boolean;
  featureHref?: string | null;
  /** Arizona feature node — lockup bylines / dek / share / comments. */
  arizonaMeta?: ArizonaSlideMeta_feedContentResource$key | null;
}

/**
 * Arizona chapter. Desktop: full-stage main (center X wipe); BR irrigation +
 * meta (bottom→up); BL still (left→right); TL still + dek (top→down); hed SVG
 * synced with main, holding over the right span.
 * Narrow: dek/meta plate matches Utah (right→left). Irrigation is the right
 * half of the band above it (right→left); two stills split the left half
 * (top→down, bottom→up). The main cell and a full-cell hed wipe bottom→up
 * through the space above that band.
 */
const ArizonaSlide: FunctionComponent<Props> = ({
  active,
  animate,
  hold = false,
  depart = false,
  leaving = false,
  resume = false,
  muted = false,
  mediaWarm = true,
  featureHref,
  arizonaMeta = null,
}) => {
  const narrow = useHubNarrowViewport();
  const rootRef = useRef<HTMLDivElement>(null);
  const plateRef = useRef<HTMLDivElement>(null);
  const data = useFragment(
    graphql`
      fragment ArizonaSlideMeta_feedContentResource on FeedContentResourceInterface {
        authors {
          name
        }
        additionalBylines
        dek {
          plaintext
        }
        title
        id
        globalId
        chorusId
        chorusUuid
        resourceType
        permalink
        comments {
          enabled
          mode
        }
      }
    `,
    arizonaMeta,
  );
  const {
    open,
    settled,
    fillPin,
    fillLock,
    departGone,
    painted,
    mediaActive,
    mediaPlayback,
    stillsActive,
  } = useWipeChapterStage({
    active,
    animate,
    hold,
    depart,
    leaving,
    resume,
    mediaWarm,
    narrow,
  });

  // Band and main cell are sized off the content-sized plate, same as Utah.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const plate = plateRef.current;
    if (!root || !plate) {
      return;
    }
    const apply = () => {
      root.style.setProperty("--arizona-plate-h", `${plate.offsetHeight}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(plate);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--arizona-plate-h");
    };
  }, [narrow]);

  const lockup = resolveLockupCopy(data, arizonaCopy);
  const permalink =
    data?.permalink ?? featureHref ?? ARIZONA_SHARE_FALLBACK_URL;
  const title = data?.title ?? arizonaCopy.shareTitle;
  const coralId = useCskCoralStoryId({
    chorusId: data?.chorusId,
    chorusUuid: data?.chorusUuid,
    globalId: data?.globalId,
    nodeId: data?.id,
  });
  const showComments = useShowStoryComments(data?.comments ?? null);
  const arizonaChrome = active;
  // Share/comments stay mounted for the life of this slide. Gating them on
  // stage paint remounts CommentCount when the wipe starts, which replays its
  // entrance animation.
  const showMeta = Boolean(permalink);
  useCskCommentCount(showComments && data ? coralId : undefined);
  const commentsLinkUrl = useCskCommentsLinkUrl(permalink);
  const articleHref = featureHref ?? (permalink || null);

  const clipAnim = wipeClipAnimAttrs({
    animate,
    resume,
    active,
    hold,
    depart,
    open,
    fillPin,
    fillLock,
    departGone,
  });
  const clip = (fromClass: string) =>
    getBrowserInfo()?.android
      ? clsx(fromClass, open && styles.open)
      : clsx(open ? styles.open : fromClass);

  return (
    <div
      ref={rootRef}
      className={clsx(
        styles.root,
        painted && !muted && styles.rootActive,
        muted && styles.rootMuted,
        settled && styles.rootSettled,
        !animate && styles.ruled,
      )}
      data-arizona-open={arizonaChrome ? "" : undefined}
      aria-hidden={active && settled ? undefined : true}
      {...clipAnim}
    >
      {articleHref ? (
        <a className={styles.slideLink} href={articleHref}>
          <span className="sr-only">{arizonaCopy.hedAlt}</span>
        </a>
      ) : (
        <h2 className="sr-only">{arizonaCopy.hedAlt}</h2>
      )}

      {/* 1) Main — desktop center X; narrow bottom → up, stopping at the band. */}
      <div
        className={clsx(
          styles.stageShell,
          narrow && styles.leadFromBottom,
          narrow && open && styles.leadFromBottomOpen,
        )}
      >
        <div
          className={clsx(
            styles.stage,
            clip(narrow ? styles.fromBottom : styles.fromCenter),
          )}
        >
          <div className={styles.mediaFill}>
            <TitleCellMedia
              // Narrow: poster only — chapter loops + title mosaic crashed iOS.
              media={
                narrow
                  ? asStill(arizonaSlideMedia.main)
                  : arizonaSlideMedia.main
              }
              active={mediaActive}
              // Keep src on hold underlays; play only while on-stage / wiping.
              playback={mediaPlayback}
            />
          </div>
        </div>
        {/* Narrow hed rides over the main cell in its own layer — see CSS. */}
        <div
          className={clsx(
            styles.hedFill,
            clip(narrow ? styles.fromBottom : styles.fromCenter),
          )}
          aria-hidden="true"
        >
          <img
            className={styles.hedFillImg}
            src={arizonaSlideMedia.hedMobile}
            alt=""
            aria-hidden="true"
            decoding="async"
            loading="eager"
            fetchPriority={arizonaChrome ? "high" : "low"}
          />
        </div>
      </div>

      {animate && !narrow ? (
        <div
          className={clsx(styles.frame, open && styles.frameOpen)}
          aria-hidden="true"
        />
      ) : null}

      {/* 2) Irrigation — desktop bottom → up with meta; narrow right → left. */}
      <div
        className={clsx(
          styles.brShell,
          narrow ? styles.leadFromRight : styles.leadFromBottom,
          open &&
            (narrow ? styles.leadFromRightOpen : styles.leadFromBottomOpen),
        )}
      >
        <div
          className={clsx(
            styles.br,
            clip(narrow ? styles.fromRight : styles.fromBottom),
          )}
        >
          <div className={styles.mediaFill}>
            <TitleCellMedia
              // Narrow: all chapter loops → posters (see main above).
              media={
                narrow
                  ? asStill(arizonaSlideMedia.irrigation)
                  : arizonaSlideMedia.irrigation
              }
              active={mediaActive}
              playback={mediaPlayback}
            />
          </div>
          {narrow ? null : (
          <div className={styles.bylinePlate}>
            <div className={styles.bylines}>
              <p className={styles.byline}>
                {lockup.bylineLabel}{" "}
                <strong className={styles.bylineName}>{lockup.bylineName}</strong>
              </p>
              <p className={styles.byline}>
                {lockup.photoBylineLabel}{" "}
                <strong className={styles.bylineName}>
                  {lockup.photoBylineName}
                </strong>
              </p>
            </div>
            {showMeta ? (
              <div className={styles.meta}>
                <ShareButtons
                  {...(shareButtonsThevergeConfig as ComponentProps<
                    typeof ShareButtons
                  >)}
                  url={permalink}
                  title={title}
                  layout="standard"
                />
                {showComments && data && coralId ? (
                  <CommentCount
                    coralId={coralId}
                    url={commentsLinkUrl}
                    authorName={data.authors?.[0]?.name ?? ""}
                    contentType={data.resourceType ?? ""}
                    headline={title}
                    variant="lede"
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          )}
        </div>
      </div>

      {/* 3) Residents — desktop left → right; narrow bottom → up. */}
      <div
        className={clsx(
          styles.blShell,
          narrow ? styles.leadFromBottom : styles.leadFromLeft,
          open &&
            (narrow ? styles.leadFromBottomOpen : styles.leadFromLeftOpen),
        )}
      >
        <div
          className={clsx(
            styles.bl,
            clip(narrow ? styles.fromBottom : styles.fromLeft),
          )}
        >
          <TitleCellMedia
            media={arizonaSlideMedia.bl}
            active={stillsActive}
          />
        </div>
      </div>

      {/* 4) Top left — flora still + dek; top → down. */}
      <div
        className={clsx(
          styles.tlShell,
          styles.leadFromTop,
          open && styles.leadFromTopOpen,
        )}
      >
        <div
          className={clsx(styles.tl, clip(styles.fromTop))}
        >
          <div className={styles.tlPhoto}>
            <img
              className={styles.tlPhotoImg}
              src={arizonaSlideMedia.tl.src}
              alt=""
              aria-hidden="true"
              decoding="async"
              loading="eager"
              fetchPriority={stillsActive ? "high" : "low"}
            />
          </div>
          {narrow ? null : <p className={styles.dek}>{lockup.lede}</p>}
        </div>
      </div>

      {/* 5) Desktop hed — center X, right span. Narrow hed lives in the stage. */}
      {narrow ? null : (
        <div
          className={clsx(styles.hed, clip(styles.fromCenter))}
        >
          <img
            className={styles.hedImg}
            src={arizonaSlideMedia.hed}
            alt=""
            aria-hidden="true"
            decoding="async"
            loading="eager"
            fetchPriority={arizonaChrome ? "high" : "low"}
          />
        </div>
      )}

      {narrow ? (
        <div
          ref={plateRef}
          className={clsx(
            styles.plateShell,
            styles.leadFromRight,
            open && styles.leadFromRightOpen,
          )}
        >
          <div
            className={clsx(
              styles.plateCell,
              clip(styles.fromRight),
            )}
          >
            <div className={styles.bylinePlate}>
              <p className={styles.dek}>{lockup.lede}</p>
              <div className={styles.bylines}>
                <p className={styles.byline}>
                  {lockup.bylineLabel}{" "}
                  <strong className={styles.bylineName}>
                    {lockup.bylineName}
                  </strong>
                </p>
                <p className={styles.byline}>
                  {lockup.photoBylineLabel}{" "}
                  <strong className={styles.bylineName}>
                    {lockup.photoBylineName}
                  </strong>
                </p>
              </div>
              {showMeta ? (
                <div className={styles.meta}>
                  <ShareButtons
                    {...(shareButtonsThevergeConfig as ComponentProps<
                      typeof ShareButtons
                    >)}
                    url={permalink}
                    title={title}
                    layout="standard"
                  />
                  {showComments && data && coralId ? (
                    <CommentCount
                      coralId={coralId}
                      url={commentsLinkUrl}
                      authorName={data.authors?.[0]?.name ?? ""}
                      contentType={data.resourceType ?? ""}
                      headline={title}
                      variant="lede"
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ArizonaSlide;
